import React from "react";
import {setBlockType, toggleMark} from "prosemirror-commands";
import {redo as historyRedo, undo as historyUndo} from "prosemirror-history";
import {redoCommand as yRedo, undoCommand as yUndo} from "y-prosemirror";
import {wrapInList} from "prosemirror-schema-list";
import {createToggleListCommand} from "prosemirror-flat-list";
import LinkRounded from "@mui/icons-material/LinkRounded";
import LinkOffRounded from "@mui/icons-material/LinkOffRounded";
import AccountTreeRounded from "@mui/icons-material/AccountTreeRounded";
import MapRounded from "@mui/icons-material/MapRounded";
import {
    addColumnAfter,
    addColumnBefore,
    addRowAfter,
    addRowBefore,
    deleteColumn,
    deleteRow,
    deleteTable,
    mergeCells,
    splitCell,
    toggleHeaderCell,
    toggleHeaderColumn,
    toggleHeaderRow,
} from "prosemirror-tables";
import FormatBoldRounded from "@mui/icons-material/FormatBoldRounded";
import FormatItalicRounded from "@mui/icons-material/FormatItalicRounded";
import FormatListBulletedRounded from "@mui/icons-material/FormatListBulletedRounded";
import FormatListNumberedRounded from "@mui/icons-material/FormatListNumberedRounded";
import UndoRounded from "@mui/icons-material/UndoRounded";
import RedoRounded from "@mui/icons-material/RedoRounded";
import ImageRounded from "@mui/icons-material/ImageRounded";
import TableChartRounded from "@mui/icons-material/TableChartRounded";
import BorderTopRounded from "@mui/icons-material/BorderTopRounded";
import BorderBottomRounded from "@mui/icons-material/BorderBottomRounded";
import BorderLeftRounded from "@mui/icons-material/BorderLeftRounded";
import BorderRightRounded from "@mui/icons-material/BorderRightRounded";
import DeleteRounded from "@mui/icons-material/DeleteRounded";
import MergeTypeRounded from "@mui/icons-material/MergeTypeRounded";
import CallSplitRounded from "@mui/icons-material/CallSplitRounded";
import ViewColumnRounded from "@mui/icons-material/ViewColumnRounded";
import TableRowsRounded from "@mui/icons-material/TableRowsRounded";
import TableViewRounded from "@mui/icons-material/TableViewRounded";
import {
    applyLink,
    canRunCommand,
    getCurrentHeadingLevel,
    getSelectedLinkAttrs,
    insertTable,
    isMarkActive,
    isSelectionLink,
    removeLink,
    runCommand
} from "./toolbarUtils";
import {insertMapNode} from "../../helpers/insertMapNode";

export function buildActions(view, provider, fileInputRef, {collaboration = false, editorMode = 'full', currentUser = null} = {}) {
    void currentUser;
    if (!view) return [];
    const {schema} = view.state;
    const basic = editorMode === 'basic';

    // Use y-prosemirror undo/redo in collaboration mode (backed by Y.UndoManager),
    // otherwise fall back to prosemirror-history.
    const undo = collaboration ? yUndo : historyUndo;
    const redo = collaboration ? yRedo : historyRedo;

    const triggerLinkPrompt = () => {
        if (!schema.marks.link || view.state.selection.empty) return;
        const currentLink = isSelectionLink(view.state) ? getSelectedLinkAttrs(view.state)?.href : null;
        const href = window.prompt("Enter URL", currentLink || "https://");
        if (!href) return;
        runCommand(view, (state, dispatch) => applyLink(state, dispatch, href));
    };

    const bold = schema.marks.strong;
    const italic = schema.marks.em;
    const link = schema.marks.link;
    const bulletList = schema.nodes.list || schema.nodes.bullet_list;
    const orderedList = schema.nodes.list ? null : schema.nodes.ordered_list;
    const heading = schema.nodes.heading;
    const paragraph = schema.nodes.paragraph;

    // Use flat-list commands when the `list` node is present
    const toggleBullet = schema.nodes.list
        ? createToggleListCommand({kind: "bullet"})
        : (bulletList ? wrapInList(bulletList) : null);
    const toggleOrdered = schema.nodes.list
        ? createToggleListCommand({kind: "ordered"})
        : (orderedList ? wrapInList(orderedList) : null);

    return [
        {
            key: "undo",
            label: "Undo",
            icon: <UndoRounded fontSize="small"/>,
            active: () => false,
            enabled: () => canRunCommand(view, undo),
            run: () => runCommand(view, undo),
        },
        {
            key: "redo",
            label: "Redo",
            icon: <RedoRounded fontSize="small"/>,
            active: () => false,
            enabled: () => canRunCommand(view, redo),
            run: () => runCommand(view, redo),
        },
        {
            key: "heading_select",
            type: "select",
            label: "Heading",
            options: [
                {value: 0, label: "Paragraph"},
                {value: 1, label: "Heading 1"},
                {value: 2, label: "Heading 2"},
                {value: 3, label: "Heading 3"},
                {value: 4, label: "Heading 4"},
                {value: 5, label: "Heading 5"},
                {value: 6, label: "Heading 6"},
            ],
            current: () => getCurrentHeadingLevel(view.state),
            enabled: () => true,
            run: (level) => {
                if (!view) return;
                if (level === 0) {
                    if (paragraph) {
                        runCommand(view, setBlockType(paragraph));
                    }
                } else if (heading) {
                    runCommand(view, setBlockType(heading, {level}));
                }
            },
        },
        {key: "separator_heading", separator: true},
        {
            key: "bold",
            label: "Bold",
            icon: <FormatBoldRounded fontSize="small"/>,
            active: bold ? () => isMarkActive(view.state, bold) : () => false,
            enabled: bold ? () => canRunCommand(view, toggleMark(bold)) : () => false,
            run: bold ? () => runCommand(view, toggleMark(bold)) : () => {
            },
        },
        {
            key: "italic",
            label: "Italic",
            icon: <FormatItalicRounded fontSize="small"/>,
            active: italic ? () => isMarkActive(view.state, italic) : () => false,
            enabled: italic ? () => canRunCommand(view, toggleMark(italic)) : () => false,
            run: italic ? () => runCommand(view, toggleMark(italic)) : () => {
            },
        },
        {
            key: "add_link",
            label: "Add/Edit Link",
            icon: <LinkRounded fontSize="small"/>,
            active: link ? () => isSelectionLink(view.state) : () => false,
            enabled: link ? () => !view.state.selection.empty : () => false,
            run: link ? triggerLinkPrompt : () => {
            },
        },
        {
            key: "remove_link",
            label: "Remove Link",
            icon: <LinkOffRounded fontSize="small"/>,
            active: () => false,
            enabled: link ? () => isSelectionLink(view.state) : () => false,
            run: link ? () => runCommand(view, removeLink) : () => {
            },
        },
        ...(!basic ? [{
            key: "link_entity",
            label: "Link Entity",
            icon: <AccountTreeRounded fontSize="small"/>,
            active: () => false,
            enabled: () => !view.state.selection.empty,
            run: () => {
                // This will be handled by the Editor component
                // Dispatch a custom event that the Editor will listen for
                const event = new CustomEvent('openEntitySearch', {
                    detail: {view}
                });
                window.dispatchEvent(event);
            },
        }] : []),
        {key: "separator_entity", separator: true},
        {
            key: "bullet_list",
            label: "Bullets",
            icon: <FormatListBulletedRounded fontSize="small"/>,
            active: () => false,
            enabled: toggleBullet ? () => canRunCommand(view, toggleBullet) : () => false,
            run: toggleBullet ? () => runCommand(view, toggleBullet) : () => {},
        },
        {
            key: "ordered_list",
            label: "Numbered",
            icon: <FormatListNumberedRounded fontSize="small"/>,
            active: () => false,
            enabled: toggleOrdered ? () => canRunCommand(view, toggleOrdered) : () => false,
            run: toggleOrdered ? () => runCommand(view, toggleOrdered) : () => {},
        },
        {
            key: "upload_image",
            label: "Upload Image",
            icon: <ImageRounded fontSize="small"/>,
            active: () => false,
            enabled: () => Boolean(provider),
            run: () => {
                if (!provider?.ws) {
                    alert('Wait for the editor to finish connecting, then try uploading the image again.');
                    return;
                }
                fileInputRef.current?.click();
            },
        },
        {key: "separator1", separator: true},
        {
            key: "insert_map",
            label: "Insert Map",
            icon: <MapRounded fontSize="small"/>,
            active: () => false,
            enabled: () => !!view.state.schema.nodes.map,
            run: () => { insertMapNode(view); },
        },
        {key: "separator2", separator: true},
        {
            key: "table_menu",
            type: "table_menu",
            label: "Table",
            icon: <TableChartRounded fontSize="small"/>,
            // Table menu items will be rendered in a dropdown
            items: [
                {
                    key: "insert_table",
                    label: "Insert Table",
                    icon: <TableChartRounded fontSize="small"/>,
                    enabled: () => canRunCommand(view, insertTable),
                    run: () => runCommand(view, insertTable),
                },
                {separator: true, key: "sep_rows"},
                {
                    key: "add_row_before",
                    label: "Add Row Before",
                    icon: <BorderTopRounded fontSize="small"/>,
                    enabled: () => canRunCommand(view, addRowBefore),
                    run: () => runCommand(view, addRowBefore),
                },
                {
                    key: "add_row_after",
                    label: "Add Row After",
                    icon: <BorderBottomRounded fontSize="small"/>,
                    enabled: () => canRunCommand(view, addRowAfter),
                    run: () => runCommand(view, addRowAfter),
                },
                {
                    key: "delete_row",
                    label: "Delete Row",
                    icon: <DeleteRounded fontSize="small"/>,
                    enabled: () => canRunCommand(view, deleteRow),
                    run: () => runCommand(view, deleteRow),
                },
                {separator: true, key: "sep_cols"},
                {
                    key: "add_col_before",
                    label: "Add Column Before",
                    icon: <BorderLeftRounded fontSize="small"/>,
                    enabled: () => canRunCommand(view, addColumnBefore),
                    run: () => runCommand(view, addColumnBefore),
                },
                {
                    key: "add_col_after",
                    label: "Add Column After",
                    icon: <BorderRightRounded fontSize="small"/>,
                    enabled: () => canRunCommand(view, addColumnAfter),
                    run: () => runCommand(view, addColumnAfter),
                },
                {
                    key: "delete_col",
                    label: "Delete Column",
                    icon: <DeleteRounded fontSize="small"/>,
                    enabled: () => canRunCommand(view, deleteColumn),
                    run: () => runCommand(view, deleteColumn),
                },
                {separator: true, key: "sep_table"},
                {
                    key: "merge_cells",
                    label: "Merge Cells",
                    icon: <MergeTypeRounded fontSize="small"/>,
                    enabled: () => canRunCommand(view, mergeCells),
                    run: () => runCommand(view, mergeCells),
                },
                {
                    key: "split_cell",
                    label: "Split Cell",
                    icon: <CallSplitRounded fontSize="small"/>,
                    enabled: () => canRunCommand(view, splitCell),
                    run: () => runCommand(view, splitCell),
                },
                {separator: true, key: "sep_headers"},
                {
                    key: "toggle_header_row",
                    label: "Toggle Header Row",
                    icon: <TableRowsRounded fontSize="small"/>,
                    enabled: () => canRunCommand(view, toggleHeaderRow),
                    run: () => runCommand(view, toggleHeaderRow),
                },
                {
                    key: "toggle_header_col",
                    label: "Toggle Header Column",
                    icon: <ViewColumnRounded fontSize="small"/>,
                    enabled: () => canRunCommand(view, toggleHeaderColumn),
                    run: () => runCommand(view, toggleHeaderColumn),
                },
                {
                    key: "toggle_header_cell",
                    label: "Toggle Header Cell",
                    icon: <TableViewRounded fontSize="small"/>,
                    enabled: () => canRunCommand(view, toggleHeaderCell),
                    run: () => runCommand(view, toggleHeaderCell),
                },
                {separator: true, key: "sep_delete"},
                {
                    key: "delete_table",
                    label: "Delete Table",
                    icon: <DeleteRounded fontSize="small"/>,
                    enabled: () => canRunCommand(view, deleteTable),
                    run: () => runCommand(view, deleteTable),
                },
            ],
        },
    ];
}

// Allow consumers to bind Ctrl/Cmd+K to open the link prompt
export function handleLinkShortcut(view, event) {
    if (!view) return false;
    if ((event.metaKey || event.ctrlKey) && !event.shiftKey && event.key.toLowerCase() === "k") {
        const schema = view.state.schema;
        if (!schema.marks.link || view.state.selection.empty) return false;
        const currentLink = isSelectionLink(view.state) ? getSelectedLinkAttrs(view.state)?.href : null;
        const href = window.prompt("Enter URL", currentLink || "https://");
        if (!href) return true;
        runCommand(view, (state, dispatch) => applyLink(state, dispatch, href));
        return true;
    }
    return false;
}
